import ClearAllRoundedIcon from "@mui/icons-material/ClearAllRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { LogEvent, LogEventKind } from "@/features/network-topology/types";
import { useNetworkActions, useNetworkTopology } from "@/features/network-topology/useNetworkTopology";

/**
 * 主要イベントログパネル（参照画像の "Key Events" 相当）。
 *
 * sim → network 由来のイベント（コンベア停止、稼働開始等）と
 * network 操作イベント（切断・接続・追加・削除）を時系列で表示する。
 */

function iconFor(kind: LogEventKind) {
  if (kind === "error") return <ErrorOutlineRoundedIcon fontSize="small" color="error" />;
  if (kind === "warning") return <WarningAmberRoundedIcon fontSize="small" color="warning" />;
  return <InfoOutlinedIcon fontSize="small" color="info" />;
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export function EventsLogPanel() {
  const { events } = useNetworkTopology();
  const { clearEvents } = useNetworkActions();

  return (
    <Stack sx={{ p: 2, gap: 1, height: "100%", overflow: "hidden" }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Key Events
        </Typography>
        <Button size="small" startIcon={<ClearAllRoundedIcon />} onClick={clearEvents} disabled={events.length === 0}>
          消去
        </Button>
      </Stack>
      <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
        {events.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            （イベントなし）
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {events.map((ev: LogEvent) => (
              <Stack
                key={ev.id}
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "flex-start",
                  borderLeft: "3px solid",
                  borderColor:
                    ev.kind === "error" ? "error.main" : ev.kind === "warning" ? "warning.main" : "info.main",
                  pl: 1,
                  py: 0.25,
                }}
              >
                {iconFor(ev.kind)}
                <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {formatTime(ev.at)}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    {ev.message}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
